import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandLess,
  ExpandMore,
  Category as CategoryIcon,
  Speed as UnitIcon,
  Receipt as TaxIcon,
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import axiosInstance from '../../utils/axios';
import { toast } from 'react-hot-toast';

const ProductImport = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      // Validate file type
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!validTypes.includes(selectedFile.type) && !selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null); // Clear previous results
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setUploading(true);
      const response = await axiosInstance.get('/products/import/template', {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file to import');
      return;
    }

    try {
      setImporting(true);

      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post('/products/import/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);

      if (response.data.successCount > 0) {
        toast.success(`Successfully imported ${response.data.successCount} products`);
      }

      if (response.data.failureCount > 0) {
        toast.warning(`${response.data.failureCount} products failed to import`);
      }

      // Clear file after successful import
      if (response.data.failureCount === 0) {
        setFile(null);
        // Reset file input
        const fileInput = document.getElementById('file-input');
        if (fileInput) fileInput.value = '';
      }

    } catch (error) {
      console.error('Error importing products:', error);
      toast.error(error.response?.data?.message || 'Failed to import products');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Import Products
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Import multiple products from an Excel file. Categories, units, and taxes will be created automatically if they don't exist.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Instructions Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Instructions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="1. Download Template"
                      secondary="Get the Excel template with required columns"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="2. Fill Data"
                      secondary="Add your products following the sample format"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="3. Upload File"
                      secondary="Select and upload your completed Excel file"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="4. Review Results"
                      secondary="Check the import summary and any errors"
                    />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Required Fields:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <InfoIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Product Name, Category, Selling Price, Unit" />
                  </ListItem>
                </List>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Optional Fields:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  SKU (Auto-generated if empty), Product Code (Remark), HSN Code, MRP, Purchase Price, Tax (%), Quantity, Reorder Level, Status, Supplier
                </Typography>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Auto-Create:</strong> If you specify a category, unit, or tax that doesn't exist,
                    it will be automatically created for you.
                  </Typography>
                </Alert>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="caption">
                    <strong>Supplier:</strong> Enter the exact supplier name as it appears in the supplier list.
                    Supplier must already exist; it will not be created automatically.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>

          {/* Upload Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Upload Excel File
                </Typography>
                <Divider sx={{ mb: 3 }} />

                {/* Download Template Button */}
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadTemplate}
                    disabled={uploading}
                    fullWidth
                    size="large"
                  >
                    {uploading ? 'Downloading...' : 'Download Import Template'}
                  </Button>
                </Box>

                {/* File Upload */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: file ? 'primary.main' : 'divider',
                    bgcolor: file ? 'primary.50' : 'background.paper',
                  }}
                >
                  <input
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    id="file-input"
                    type="file"
                    onChange={handleFileSelect}
                  />
                  <label htmlFor="file-input">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<UploadIcon />}
                      size="large"
                    >
                      Select Excel File
                    </Button>
                  </label>

                  {file && (
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label={file.name}
                        onDelete={() => setFile(null)}
                        color="primary"
                        sx={{ maxWidth: '100%' }}
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 1 }} color="text.secondary">
                        {(file.size / 1024).toFixed(2)} KB
                      </Typography>
                    </Box>
                  )}
                </Paper>

                {/* Import Button */}
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={handleImport}
                  disabled={!file || importing}
                  startIcon={importing ? <CircularProgress size={20} /> : <UploadIcon />}
                >
                  {importing ? 'Importing...' : 'Import Products'}
                </Button>

                {/* Import Results */}
                {result && (
                  <Box sx={{ mt: 3 }}>
                    <Alert
                      severity={result.failureCount === 0 ? 'success' : 'warning'}
                      icon={result.failureCount === 0 ? <SuccessIcon /> : <ErrorIcon />}
                    >
                      <Typography variant="body1" gutterBottom>
                        {result.message}
                      </Typography>
                    </Alert>

                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                      <Grid item xs={4}>
                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                          <Typography variant="h4" color="primary">{result.totalRows}</Typography>
                          <Typography variant="caption" color="text.secondary">Total Rows</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                          <Typography variant="h4" color="success.main">{result.successCount}</Typography>
                          <Typography variant="caption" color="text.secondary">Successful</Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                          <Typography variant="h4" color="error.main">{result.failureCount}</Typography>
                          <Typography variant="caption" color="text.secondary">Failed</Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {/* Created Entities */}
                    {(result.createdCategories.length > 0 || result.createdUnits.length > 0 || result.createdTaxes.length > 0) && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Automatically Created:
                        </Typography>
                        <Grid container spacing={2}>
                          {result.createdCategories.length > 0 && (
                            <Grid item xs={12}>
                              <Paper sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <CategoryIcon color="primary" />
                                  <Typography variant="subtitle2">
                                    Categories ({result.createdCategories.length}):
                                  </Typography>
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                  {result.createdCategories.map((cat, idx) => (
                                    <Chip key={idx} label={cat} size="small" sx={{ m: 0.5 }} />
                                  ))}
                                </Box>
                              </Paper>
                            </Grid>
                          )}

                          {result.createdUnits.length > 0 && (
                            <Grid item xs={12}>
                              <Paper sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <UnitIcon color="primary" />
                                  <Typography variant="subtitle2">
                                    Units ({result.createdUnits.length}):
                                  </Typography>
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                  {result.createdUnits.map((unit, idx) => (
                                    <Chip key={idx} label={unit} size="small" sx={{ m: 0.5 }} />
                                  ))}
                                </Box>
                              </Paper>
                            </Grid>
                          )}

                          {result.createdTaxes.length > 0 && (
                            <Grid item xs={12}>
                              <Paper sx={{ p: 2 }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <TaxIcon color="primary" />
                                  <Typography variant="subtitle2">
                                    Taxes ({result.createdTaxes.length}):
                                  </Typography>
                                </Box>
                                <Box sx={{ mt: 1 }}>
                                  {result.createdTaxes.map((tax, idx) => (
                                    <Chip key={idx} label={tax} size="small" sx={{ m: 0.5 }} />
                                  ))}
                                </Box>
                              </Paper>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {/* Errors Table */}
                    {result.errors && result.errors.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          onClick={() => setShowErrors(!showErrors)}
                          sx={{ cursor: 'pointer', mb: 1 }}
                        >
                          <Typography variant="subtitle1" color="error">
                            Errors ({result.errors.length})
                          </Typography>
                          {showErrors ? <ExpandLess /> : <ExpandMore />}
                        </Box>

                        <Collapse in={showErrors}>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Row</TableCell>
                                  <TableCell>Product</TableCell>
                                  <TableCell>Field</TableCell>
                                  <TableCell>Error</TableCell>
                                  <TableCell>Details</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {result.errors.map((error, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{error.rowNumber}</TableCell>
                                    <TableCell>{error.productName || '-'}</TableCell>
                                    <TableCell>{error.field || '-'}</TableCell>
                                    <TableCell>{error.error}</TableCell>
                                    <TableCell>{error.details || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default ProductImport;

